import React from 'react';
import { 
    TextField, 
    Button, 
    Checkbox, 
    Slider, 
    Select, 
    MenuItem, 
    FormControl, 
    InputLabel, 
    FormControlLabel, 
    Grid, 
    Box, 
    Tooltip,
    Typography,
    Card,
    CardContent,
    useTheme,
    useMediaQuery,
    IconButton,
    Collapse
} from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import SettingsIcon from '@mui/icons-material/Settings';
import DisplaySettingsIcon from '@mui/icons-material/DisplaySettings';
import TuneIcon from '@mui/icons-material/Tune';
import MovementZones from '../../../../components/Settings/MovementZones';

// Reusable Setting Item Component with help tooltip
const SettingItem = ({ helpText, children }) => {
    const [showHelp, setShowHelp] = React.useState(false);
    
    return (
        <Box sx={{ 
            width: '100%',
            mb: { xs: 1, sm: 1.5 }
        }}>
            <Box sx={{ 
                display: 'flex', 
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: 1
            }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    {children}
                </Box>
                {helpText && (
                    <IconButton 
                        size="small" 
                        onClick={() => setShowHelp(!showHelp)}
                        sx={{ 
                            mt: 0.5,
                            color: 'action.active',
                            '&:hover': { color: 'primary.main' }
                        }}
                    >
                        <HelpOutlineIcon fontSize="small" />
                    </IconButton>
                )}
            </Box>
            {helpText && (
                <Collapse in={showHelp}>
                    <Typography 
                        variant="caption" 
                        sx={{ 
                            display: 'block',
                            color: 'text.secondary',
                            bgcolor: 'action.hover',
                            borderRadius: 1,
                            p: 1,
                            mt: 0.5,
                            lineHeight: 1.4
                        }}
                    >
                        {helpText}
                    </Typography>
                </Collapse>
            )}
        </Box>
    );
};

// Section Card Component
const SettingsSection = ({ title, icon: Icon, children }) => {
    return (
        <Card 
            elevation={0}
            sx={{ 
                mb: { xs: 2, sm: 3 },
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                overflow: 'hidden'
            }}
        >
            <Box 
                sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1.5,
                    px: { xs: 2, sm: 2.5 },
                    py: { xs: 1.25, sm: 1.5 },
                    bgcolor: 'grey.50',
                    borderBottom: '1px solid',
                    borderColor: 'divider'
                }}
            >
                {Icon && <Icon sx={{ fontSize: { xs: 18, sm: 20 }, color: 'primary.main' }} />}
                <Typography 
                    variant="subtitle2" 
                    sx={{ 
                        fontWeight: 600,
                        color: 'text.primary',
                        fontSize: { xs: '0.875rem', sm: '0.9rem' }
                    }}
                >
                    {title}
                </Typography>
            </Box>
            <CardContent sx={{ 
                px: { xs: 2, sm: 2.5 },
                py: { xs: 2, sm: 2 },
                '&:last-child': { pb: { xs: 2, sm: 2 } }
            }}>
                {children}
            </CardContent>
        </Card>
    );
};

// Responsive TextField Component
const ResponsiveTextField = ({ name, value, onChange, label, disabled, helpText, type = 'text', multiline = false, maxRows }) => {
    return (
        <SettingItem helpText={helpText}>
            <TextField
                fullWidth
                size="medium"
                label={label}
                name={name}
                value={value || ''}
                onChange={onChange}
                variant="outlined"
                disabled={disabled}
                type={type}
                multiline={multiline}
                maxRows={maxRows}
                sx={{
                    '& .MuiInputBase-input': {
                        fontSize: { xs: '1rem', sm: '0.95rem' },
                        py: { xs: 1.5, sm: 1.25 }
                    },
                    '& .MuiInputLabel-root': {
                        fontSize: { xs: '0.95rem', sm: '0.9rem' }
                    }
                }}
            />
        </SettingItem>
    );
};

// Responsive Checkbox Component
const ResponsiveCheckbox = ({ name, checked, onChange, label, disabled, helpText, warning }) => {
    return (
        <SettingItem helpText={helpText}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <FormControlLabel
                    control={
                        <Checkbox 
                            name={name}
                            checked={checked || false}
                            onChange={onChange}
                            disabled={disabled}
                            sx={{
                                '& .MuiSvgIcon-root': {
                                    fontSize: { xs: 24, sm: 24 }
                                }
                            }}
                        />
                    }
                    label={
                        <Typography 
                            variant="body2" 
                            sx={{ 
                                fontSize: { xs: '0.875rem', sm: '0.9rem' },
                                color: disabled ? 'text.disabled' : 'text.primary'
                            }}
                        >
                            {label}
                        </Typography>
                    }
                    sx={{
                        m: 0,
                        flex: 1,
                        '& .MuiFormControlLabel-label': {
                            flex: 1
                        }
                    }}
                />
                {warning && (
                    <Tooltip title={warning}>
                        <WarningIcon color='error' sx={{ ml: 1, fontSize: 20 }} />
                    </Tooltip>
                )}
            </Box>
        </SettingItem>
    );
};

const ANPRTab = ({ 
    settings, 
    handleInputChange,
    kiosks,
    disableNumberPlateAliases,
    masterPlateAliases,
    masterTenantNameForNumberPlates,
    showANPRMovementZonesDialog,
    setShowANPRMovementZonesDialog
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    return (
        <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
            {/* Enable ANPR Section */}
            <SettingsSection title="ANPR Settings" icon={DirectionsCarIcon}>
                <Grid container spacing={{ xs: 2, sm: 3 }}>
                    <Grid item xs={12}>
                        <ResponsiveCheckbox
                            name="SettingEnableANPR"
                            checked={settings.SettingEnableANPR}
                            onChange={handleInputChange}
                            label="Enable ANPR"
                            helpText="Enables the Automatic Number Plate Recognition functionality on this kiosk"
                        />
                    </Grid>
                </Grid>
            </SettingsSection>

            {/* Background Scanning Settings */}
            <SettingsSection title="Background Scanning Settings" icon={SettingsIcon}>
                <Grid container spacing={{ xs: 2, sm: 3 }}>
                    <Grid item xs={12} sm={6}>
                        <SettingItem helpText="Adjust the sensitivity of movement detection to reduce network traffic. Lower = more movement required to trigger a scan.">
                            <Typography 
                                variant="body2" 
                                gutterBottom 
                                sx={{ mb: 1 }}
                            >
                                Movement sensitivity for scanning (ANPR & QR Codes)
                            </Typography>
                            <Box sx={{ px: 1 }}>
                                <Slider
                                    name="SettingANPRSensitivity"
                                    value={settings.SettingANPRSensitivity || 15000000}
                                    onChange={handleInputChange}
                                    min={5000000}
                                    max={30000000}
                                    sx={{
                                        '& .MuiSlider-thumb': {
                                            width: { xs: 20, sm: 16 },
                                            height: { xs: 20, sm: 16 }
                                        }
                                    }}
                                />
                            </Box>
                        </SettingItem>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Box sx={{ pt: { xs: 0, sm: 2 } }}>
                            <Button 
                                variant="outlined" 
                                onClick={() => setShowANPRMovementZonesDialog(true)}
                                disabled={!settings.SettingEnableANPR}
                                fullWidth={isMobile}
                                sx={{ 
                                    py: { xs: 1.25, sm: 1 },
                                    fontSize: { xs: '0.9rem', sm: '0.875rem' }
                                }}
                            >
                                Configure Movement Zones
                            </Button>
                        </Box>
                    </Grid>
                </Grid>
            </SettingsSection>

            {/* ANPR Display Settings */}
            <SettingsSection title="ANPR Display Settings" icon={DisplaySettingsIcon}>
                <Grid container spacing={{ xs: 2, sm: 3 }}>
                    <Grid item xs={12} sm={6}>
                        <SettingItem helpText="Mirror ANPR scan messages on another device. Useful for rear-camera setups where you want messages to display on a front-facing kiosk.">
                            <FormControl variant="outlined" fullWidth>
                                <InputLabel id="mirror-anpr-label">Mirror ANPR messages to device</InputLabel>
                                <Select
                                    labelId="mirror-anpr-label"
                                    value={settings.SettingMirrorANPRWelcomeMessageOnTenantId || 'none'}
                                    onChange={handleInputChange}
                                    label="Mirror ANPR messages to device"
                                    name="SettingMirrorANPRWelcomeMessageOnTenantId"
                                    disabled={!settings.SettingEnableANPR}
                                    sx={{
                                        '& .MuiSelect-select': {
                                            fontSize: { xs: '1rem', sm: '0.95rem' },
                                            py: { xs: 1.5, sm: 1.25 }
                                        }
                                    }}
                                >
                                    {kiosks.map((kiosk) => (
                                        <MenuItem key={kiosk.id} value={kiosk.id}>
                                            {kiosk.OrganisationName}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </SettingItem>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <ResponsiveCheckbox
                            name="SettingDoNotShowANPRWelcomeMessage"
                            checked={settings.SettingDoNotShowANPRWelcomeMessage}
                            onChange={handleInputChange}
                            label="Do not show ANPR welcome message on this kiosk"
                            disabled={!settings.SettingEnableANPR}
                            helpText="Hide the welcome message on this kiosk. Useful when mirroring to another kiosk."
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <SettingItem helpText="For 'Dedicated ANPR Camera' device type. Choose what to display on the ANPR UI. Leave blank for blank screen.">
                            <TextField
                                fullWidth
                                label="ANPR Message"
                                name="SettingANPRMessage"
                                value={settings.SettingANPRMessage || ''}
                                onChange={handleInputChange}
                                variant="outlined"
                                multiline
                                disabled={!settings.SettingEnableANPR}
                                maxRows={10}
                                minRows={3}
                                onKeyDown={(e) => {
                                    if (e.key === 'Tab') {
                                        e.preventDefault();
                                        const textarea = e.target;
                                        const start = textarea.selectionStart;
                                        const end = textarea.selectionEnd;
                                        textarea.value = textarea.value.substring(0, start) + '\t' + textarea.value.substring(end);
                                        textarea.selectionStart = textarea.selectionEnd = start + 1;
                                    }
                                }}
                                sx={{
                                    '& .MuiInputBase-input': {
                                        fontSize: { xs: '0.95rem', sm: '0.9rem' }
                                    }
                                }}
                            />
                        </SettingItem>
                    </Grid>
                </Grid>
            </SettingsSection>

            {/* ANPR Configuration */}
            <SettingsSection title="ANPR Configuration" icon={TuneIcon}>
                <Grid container spacing={{ xs: 2, sm: 3 }}>
                    <Grid item xs={12} sm={6}>
                        <ResponsiveTextField
                            name="SettingANPRSecondsBeforeReads"
                            value={settings.SettingANPRSecondsBeforeReads}
                            onChange={handleInputChange}
                            label="Wait time (seconds) between scans"
                            disabled={!settings.SettingEnableANPR}
                            helpText="Time before rescanning the same number plate. Helps prevent duplicate scans."
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <ResponsiveTextField
                            name="SettingANPRWaitTimeMilliseconds"
                            value={settings.SettingANPRWaitTimeMilliseconds}
                            onChange={handleInputChange}
                            label="Wait time (ms) before picture taken"
                            disabled={!settings.SettingEnableANPR}
                            helpText="Milliseconds delay before taking photo. Can help avoid blurry images."
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <ResponsiveCheckbox
                            name="SettingANPRUnrecognisedVehicleWorkflow"
                            checked={settings.SettingANPRUnrecognisedVehicleWorkflow}
                            onChange={handleInputChange}
                            label="Enable unrecognised vehicle workflow"
                            disabled={!settings.SettingEnableANPR}
                            helpText="When an unknown plate is detected, alert app users who can then open the gate or show a custom message."
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <SettingItem helpText="Who receives notifications for unrecognised vehicles">
                            <FormControl variant="outlined" fullWidth>
                                <InputLabel id="notification-option-label">Notification option</InputLabel>
                                <Select
                                    labelId="notification-option-label"
                                    value={settings.SettingANPRUnrecognisedNotificationOption || 'none'}
                                    onChange={handleInputChange}
                                    label="Notification option"
                                    name="SettingANPRUnrecognisedNotificationOption"
                                    disabled={!settings.SettingANPRUnrecognisedVehicleWorkflow || !settings.SettingEnableANPR}
                                    sx={{
                                        '& .MuiSelect-select': {
                                            fontSize: { xs: '1rem', sm: '0.95rem' },
                                            py: { xs: 1.5, sm: 1.25 }
                                        }
                                    }}
                                >
                                    <MenuItem value='1'>All Kiosk Admins</MenuItem>
                                    <MenuItem value='2'>All Kiosk Responders</MenuItem>
                                </Select>
                            </FormControl>
                        </SettingItem>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <ResponsiveCheckbox
                            name="SettingAllowAliasesForNumberPlates"
                            checked={masterPlateAliases ?? (settings.SettingAllowAliasesForNumberPlates || false)}
                            onChange={handleInputChange}
                            label="Enable aliases for number plates"
                            disabled={!settings.SettingEnableANPR || disableNumberPlateAliases}
                            helpText="Assign number plates to Aliases to notify specific people on successful ANPR entry."
                            warning={disableNumberPlateAliases ? `Number Plates are shared from ${masterTenantNameForNumberPlates}. Switch to that kiosk to change this setting.` : null}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <ResponsiveCheckbox
                            name="SettingSendMultipleANPRSnapshots"
                            checked={settings.SettingSendMultipleANPRSnapshots}
                            onChange={handleInputChange}
                            label="Send multiple ANPR snapshots"
                            disabled={!settings.SettingEnableANPR}
                            helpText="Send multiple photos to improve read success. Use only if having issues. Has bandwidth implications."
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <ResponsiveTextField
                            name="SettingSendMultipleANPRSnapshots_SecondsBetween"
                            value={settings.SettingSendMultipleANPRSnapshots_SecondsBetween}
                            onChange={handleInputChange}
                            label="Seconds between snapshots"
                            disabled={!settings.SettingSendMultipleANPRSnapshots}
                            helpText="Time interval between each snapshot"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <ResponsiveTextField
                            name="SettingSendMultipleANPRSnapshots_MaximumSnapshots"
                            value={settings.SettingSendMultipleANPRSnapshots_MaximumSnapshots}
                            onChange={handleInputChange}
                            label="Maximum number of snapshots"
                            disabled={!settings.SettingSendMultipleANPRSnapshots}
                            helpText="Maximum snapshots to send per detection"
                        />
                    </Grid>
                </Grid>
            </SettingsSection>

            <MovementZones
                open={showANPRMovementZonesDialog}
                onClose={() => setShowANPRMovementZonesDialog(false)}
                zoneType="anpr"
            />
        </Box>
    );
};

export default ANPRTab;
